import { Component } from '@theme/component';

/**
 * How long the bar stays a polite live region after a visitor-initiated slide change.
 * Long enough for assistive tech to pick up the new slide, short enough that the next
 * automatic rotation is never announced.
 */
const ANNOUNCE_DURATION = 1000;

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The timeout ID that resets the live region back to `off`.
   * @type {number|undefined}
   */
  #announceTimeout = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    // Keyboard equivalent of the hover pause — rotation must not move content out from
    // under someone who has tabbed into the bar.
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    clearTimeout(this.#announceTimeout);
  }

  /**
   * @param {Event} [event] - Only present when a visitor activated the next arrow.
   */
  next(event) {
    if (event) this.#handleUserNavigation();
    this.current += 1;
  }

  /**
   * @param {Event} [event] - Only present when a visitor activated the previous arrow.
   */
  previous(event) {
    if (event) this.#handleUserNavigation();
    this.current -= 1;
  }

  /**
   * Announces a slide change the visitor asked for.
   *
   * Auto-rotating promotional text must not be announced — it interrupts whatever the
   * visitor is reading — but a change they asked for should be. Per the WAI-ARIA APG
   * carousel pattern the live region stays `off` while rotating and becomes `polite`
   * only around a user-initiated change.
   *
   * Navigating does not stop rotation: the arrows are labelled "next"/"previous", so
   * silently killing autoplay from them left no way to tell it had happened or to undo
   * it. Hovering or focusing the bar already suspends rotation for as long as the
   * visitor is interacting with it.
   */
  #handleUserNavigation() {
    clearTimeout(this.#announceTimeout);
    this.setAttribute('aria-live', 'polite');
    this.#announceTimeout = setTimeout(
      () => this.setAttribute('aria-live', 'off'),
      ANNOUNCE_DURATION
    );
  }

  /**
   * Starts or stops auto-rotation on the visitor's request.
   *
   * Hovering or focusing the bar only suspends rotation for as long as the visitor stays
   * there; WCAG 2.2.2 wants a mechanism that stops it outright. An explicit request wins
   * over the hover/focus suspension — pressing play means play.
   *
   * @param {Event} [event]
   */
  toggleAutoplay(event) {
    event?.preventDefault();

    if (this.paused) this.play();
    else this.pause();

    this.#syncAutoplayToggle();
  }

  /**
   * Keeps the toggle's accessible name describing what pressing it will do.
   */
  #syncAutoplayToggle() {
    const toggle = this.querySelector('.announcement-bar__autoplay-toggle');
    if (!(toggle instanceof HTMLElement)) return;

    const label = this.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    // Never stack timers: play() is also how rotation restarts.
    this.suspend();
    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Stop the timer while the page is hidden.
   *
   * `suspend()`, not `pause()`: `paused` means "the visitor stopped it", and `resume()`
   * refuses to restart once it is set — so pausing here would permanently kill rotation
   * after the first tab switch.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
